/**
 * React equivalent of the ASP `Vocab(DefaultValue)` helper (utilclients.asp):
 * a site renames a field label by adding a row to the Vocabulary table
 * (`English` = default label, `Other` = replacement). A row applies to every
 * screen that shows that label, as in PCI.
 */

/**
 * Case/spacing-insensitive key, so "Daytime Phone", "Daytime_Phone" and
 * "daytimePhone" all match the same row.
 */
export const normalizeVocabKey = (value) =>
  String(value ?? "")
    .toLowerCase()
    .replace(/[\s_-]+/g, "");

/**
 * Turns Vocabulary rows into a lookup map: { [normalized english]: other }.
 * Done once when the rows arrive so rendering only reads from it.
 * A blank or NULL `Other` is kept as "", which hides the label, matching
 * ASP's `nz(rsConfig.Fields("Other"))`.
 *
 * @param {Array<{english: string, other: string}>} rows
 */
export const buildVocabMap = (rows) => {
  const map = {};
  (rows || []).forEach((row) => {
    const key = normalizeVocabKey(row?.english);
    if (!key) return;
    map[key] = typeof row?.other === "string" ? row.other : "";
  });
  return map;
};

/**
 * Returns the site's replacement for a label, trying the label text first and
 * then the field name, or the original label when neither has a row. The
 * field-name fallback mirrors ASP's OverrideFieldDescription, which let a site
 * rename a field by its DB field name (e.g. "Serve_24_7") rather than its text.
 *
 * @param {object} map         - from buildVocabMap
 * @param {string} label       - default label text
 * @param {string|Array} [fieldName] - e.g. "serve247", or a form path such as
 *   ["inventoryItems", 0, "storageLocation2Id"] (its last part is used)
 * @returns {string}
 */
export const resolveVocab = (map, label, fieldName) => {
  if (!map || typeof label !== "string") return label;

  const field = Array.isArray(fieldName) ? fieldName[fieldName.length - 1] : fieldName;
  for (const key of [normalizeVocabKey(label), normalizeVocabKey(field)]) {
    if (key && map[key] !== undefined) return map[key];
  }
  return label;
};
